import { useMemo, useState } from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import type { GridColDef } from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { DataTable, LoadingState, useToast } from '@radiology/ui';
import { DocumentDownload } from 'iconsax-react';
import dayjs from 'dayjs';
import {
  STATUS_TONE,
  getFileUrl,
  useCertificates,
  type Certificate,
  type CertificateStatus,
} from './api';

/**
 * The one certificate table, shared by every role's screens.
 *
 * What each role sees is decided by RLS, not by props — an employee and a
 * manager render the same component and get different rows. `showOwner` only
 * controls whether the person's name is a useful column, which it is not when
 * every row is your own.
 */
export function CertificateTable({
  status,
  showOwner = true,
  emptyTitle,
  emptyDescription,
  onReview,
  owner,
  onlyIds,
}: {
  status?: CertificateStatus | CertificateStatus[];
  showOwner?: boolean;
  emptyTitle: string;
  emptyDescription?: string;
  onReview?: (certificate: Certificate) => void;
  /** The employee's own actions on their own rows. */
  owner?: {
    onReplace: (certificate: Certificate) => void;
    onWithdraw: (certificate: Certificate) => void;
  };
  /**
   * Narrow to a known set of rows.
   *
   * A display filter, never a security one — RLS has already decided what this
   * query can return. Used by "Other certificates on record", where the set
   * comes from a view that knows which types are not required.
   */
  onlyIds?: Set<string>;
}) {
  const { data, isLoading, error, refetch } = useCertificates(status);
  const [search, setSearch] = useState('');
  const toast = useToast();

  const openFile = async (fileName: string) => {
    try {
      window.open(await getFileUrl(fileName), '_blank', 'noopener');
    } catch {
      toast.error('That file could not be opened. It may not have been migrated.');
    }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data
      .filter(c => !onlyIds || onlyIds.has(c.id))
      .filter(
        c =>
          !needle ||
          [c.name, c.first_name, c.last_name, c.username, c.badge_number, c.section]
            .filter(Boolean)
            .some(v => v.toLowerCase().includes(needle)),
      );
  }, [data, search, onlyIds]);

  const columns = useMemo<GridColDef<Certificate>[]>(() => {
    const cols: GridColDef<Certificate>[] = [
      { field: 'name', headerName: 'Certificate', flex: 1.4, minWidth: 200 },
    ];

    if (showOwner) {
      cols.push({
        field: 'owner',
        headerName: 'Held by',
        flex: 1.2,
        minWidth: 180,
        valueGetter: (_v, row) => `${row.first_name} ${row.last_name}`.trim(),
        renderCell: params => (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2">{params.value as string}</Typography>
            <Typography variant="caption" color="text.secondary">
              {/* badge_number, not employee_id: the legacy app stored the
                  Keycloak user id in employee_id, so all 3,239 rows hold a UUID
                  there. badge_number is the identifier staff actually use. */}
              {params.row.badge_number} · {params.row.section}
            </Typography>
          </Stack>
        ),
      });
    }

    cols.push(
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        renderCell: params => {
          const value = params.value as CertificateStatus;
          const tone = STATUS_TONE[value];
          return (
            <Chip
              size="small"
              label={value}
              color={tone === 'default' ? undefined : tone}
              variant={tone === 'default' ? 'outlined' : 'filled'}
              sx={{ textTransform: 'capitalize' }}
            />
          );
        },
      },
      {
        field: 'expiry_date',
        headerName: 'Expires',
        width: 150,
        valueGetter: value => (value ? dayjs(value).format('D MMM YYYY') : ''),
        renderCell: params => {
          const days = dayjs(params.row.expiry_date).diff(dayjs(), 'day');
          return (
            <Stack sx={{ py: 0.5 }}>
              <Typography variant="body2">{params.value as string}</Typography>
              <Typography
                variant="caption"
                color={days < 0 ? 'error.main' : days <= 45 ? 'warning.main' : 'text.secondary'}
              >
                {days < 0 ? `${Math.abs(days)} days ago` : `in ${days} days`}
              </Typography>
            </Stack>
          );
        },
      },
    );

    cols.push({
      field: 'file',
      headerName: 'File',
      width: 70,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <Tooltip title="Open the certificate">
          <IconButton size="small" onClick={() => void openFile(params.row.file_name)}>
            <DocumentDownload size={18} color="currentColor" />
          </IconButton>
        </Tooltip>
      ),
    });

    if (onReview) {
      cols.push({
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => (
          <Button size="small" variant="outlined" onClick={() => onReview(params.row)}>
            Review
          </Button>
        ),
      });
    }

    if (owner) {
      cols.push({
        field: 'owner-actions',
        headerName: '',
        width: 130,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => {
          // Pending is the only status an employee may withdraw — once it has
          // been reviewed it is a record of a decision. Anything not currently
          // valid can be replaced with a fresh document.
          if (params.row.status === 'pending') {
            return (
              <Button size="small" color="inherit" onClick={() => owner.onWithdraw(params.row)}>
                Withdraw
              </Button>
            );
          }
          if (params.row.status === 'rejected') {
            return (
              <Button size="small" variant="outlined" onClick={() => owner.onReplace(params.row)}>
                Replace
              </Button>
            );
          }
          return null;
        },
      });
    }

    return cols;
  }, [showOwner, onReview, owner]);

  if (isLoading) return <LoadingState variant="skeleton" rows={6} />;

  return (
    <DataTable<Certificate>
      rows={rows}
      columns={columns}
      getRowHeight={() => 56}
      search={{ value: search, onChange: setSearch, placeholder: 'Search name, staff, section…' }}
      empty={{ title: emptyTitle, description: emptyDescription }}
      error={error ? { message: error.message, onRetry: () => void refetch() } : undefined}
      allowExport
    />
  );
}
